import React, { useEffect, useRef, useState } from 'react';
import icebreakerService, {
  IcebreakerChannel,
  IcebreakerQualityResult,
} from '../services/icebreakerService';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

interface IcebreakerQualityProps {
  text: string;
  kind: IcebreakerChannel;
  contact?: { firstName?: string; company?: string };
}

const ratingColor: Record<string, string> = {
  gut: 'var(--color-success, #16a34a)',
  ok: 'var(--color-warning, #b45309)',
  schwach: 'var(--color-error, #dc2626)',
};

/**
 * Inline-Qualitätsbewertung eines Icebreakers (Score + Hinweise). Bewertet den
 * aktuellen Text serverseitig (reine Heuristik, kein KI-Call), debounced, damit
 * Tippen flüssig bleibt. Leerer Text → nichts anzeigen.
 * Welle 2: POST /icebreaker/assess verlangt vertrieb.bearbeiten -- ohne das
 * Recht geht keine Anfrage raus und die Komponente zeigt nichts.
 */
const IcebreakerQuality: React.FC<IcebreakerQualityProps> = ({ text, kind, contact }) => {
  const [result, setResult] = useState<IcebreakerQualityResult | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstName = contact?.firstName;
  const company = contact?.company;
  const { darf } = useBerechtigung();
  const darfBewerten = darf('vertrieb.bearbeiten');

  useEffect(() => {
    if (!darfBewerten || !text || !text.trim()) {
      setResult(null);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        setResult(await icebreakerService.assessQuality({ text, kind, contact: { firstName, company } }));
      } catch {
        setResult(null); // Bewertung ist optional – bei Fehler einfach nichts anzeigen.
      }
    }, 450);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [darfBewerten, text, kind, firstName, company]);

  if (!result) return null;

  return (
    <div style={{ marginTop: '0.35rem' }}>
      <span
        className="text-xs"
        style={{ fontWeight: 600, color: ratingColor[result.rating] || 'inherit' }}
        title="Heuristische Qualitätsbewertung – kein KI-Call"
      >
        Qualität: {result.score}/100 ({result.rating})
      </span>
      {result.issues.length > 0 && (
        <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1rem' }}>
          {result.issues.map((issue, i) => (
            <li
              key={i}
              className="text-xs"
              style={{ color: issue.level === 'warn' ? 'var(--color-error, #dc2626)' : 'var(--color-text-muted, #666)' }}
            >
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default IcebreakerQuality;
